import React, { useEffect, useRef } from "react";

// ASCII文字セット（濃淡が分かりやすい順に並べる）
const ASCII_CHARS = "@%#*+=-:. ";

// ディスプレイ解像度を制御（上限を1.5にして重くなりすぎないように）
function clampDPR() {
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  return Math.min(dpr, 1.5);
}

export default function ASCIIText({ text = "akiba.vip", starCount = 400 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const dpr = clampDPR();
    let w = 0,
      h = 0,
      rafId = 0;

    // === 星の配列 ===
    let stars = [];
    let mouseX = 0;

    // ⭐ 星の初期化
    const initStars = () => {
      stars = [];
      for (let i = 0; i < starCount; i++) {
        const depth = Math.random(); // 0 = 遠い, 1 = 近い
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          speed: 0.3 + depth * 2.0, // 深度でスピード差を付ける
          size: depth < 0.3 ? 1 : depth < 0.7 ? 2 : 3,
          depth,
        });
      }
    };

    // === ASCIIテキストをオフスクリーンCanvasで生成 ===
    let asciiBuffer = [];
    let offscreen;

    const renderAsciiBuffer = () => {
      // 解像度を落としたオフスクリーンCanvasを作成
      offscreen = document.createElement("canvas");
      offscreen.width = w / 7;
      offscreen.height = h / 14;
      const octx = offscreen.getContext("2d");

      // 背景を黒で塗りつぶし
      octx.fillStyle = "#000";
      octx.fillRect(0, 0, offscreen.width, offscreen.height);

      // テキスト描画
      const fontSize = Math.min(offscreen.height, offscreen.width / text.length) * 0.95;
      octx.font = `bold ${fontSize}px IBM Plex Mono, monospace`;
      octx.fillStyle = "#fff";
      octx.textAlign = "center";
      octx.textBaseline = "middle";
      octx.fillText(text, offscreen.width / 2, offscreen.height / 2);

      // 文字を画像データ化してASCII変換
      const imgData = octx.getImageData(0, 0, offscreen.width, offscreen.height);
      asciiBuffer = [];

      for (let y = 0; y < offscreen.height; y++) {
        let row = "";
        for (let x = 0; x < offscreen.width; x++) {
          const idx = (y * offscreen.width + x) * 4;
          const r = imgData.data[idx];
          const g = imgData.data[idx + 1];
          const b = imgData.data[idx + 2];
          const brightness = (r + g + b) / 3 / 255;
          const charIdx = Math.floor((1 - brightness) * (ASCII_CHARS.length - 1));
          row += ASCII_CHARS[charIdx];
        }
        asciiBuffer.push(row);
      }
    };

    // === リサイズ処理 ===
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      initStars();
      renderAsciiBuffer();
    };

    // === 描画処理 ===
    const draw = () => {
      // 背景を黒に塗る
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);

      // --- 背景の星 ---
      ctx.fillStyle = "#fff";
      ctx.globalAlpha = 1.0;
      ctx.shadowBlur = 0; // 星にはシャドウ不要

      for (const s of stars) {
        // 星の明るさを深度で調整
        ctx.globalAlpha = 0.5 + s.depth * 0.5;
        ctx.fillRect(s.x, s.y, s.size, s.size);

        // 縦方向（落下する感じ）
        s.y += s.speed;
        // 横方向（マウス位置に応じて揺れる）
        s.x += ((mouseX - w / 2) / w) * 8 * s.depth;

        // 画面外に出たらループ
        if (s.y > h) s.y = 0;
        if (s.x < 0) s.x = w;
        if (s.x > w) s.x = 0;
      }

      // --- ASCII文字 ---
      const cellW = w / asciiBuffer[0].length;
      const cellH = h / asciiBuffer.length;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${Math.min(cellW, cellH) * 1.1}px IBM Plex Mono, monospace`;

      ctx.globalAlpha = 1.0;
      ctx.fillStyle = "#fff";

      // 光（グロー効果）用に一度影つきで描画
      ctx.shadowColor = "#fff";
      ctx.shadowBlur = 15;

      for (let y = 0; y < asciiBuffer.length; y++) {
        for (let x = 0; x < asciiBuffer[y].length; x++) {
          const ch = asciiBuffer[y][x];
          if (ch !== " ") {
            ctx.fillText(ch, x * cellW + cellW / 2, y * cellH + cellH / 2);
          }
        }
      }

      rafId = requestAnimationFrame(draw);
    };

    // 初期化
    resize();
    rafId = requestAnimationFrame(draw);

    // リサイズ監視
    const ro = new ResizeObserver(() => resize());
    ro.observe(canvas.parentElement);

    // マウス移動
    const onMouseMove = (e) => {
      mouseX = e.clientX;
    };
    window.addEventListener("mousemove", onMouseMove);

    // クリーンアップ
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [text, starCount]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        background: "#000",
      }}
    />
  );
}
